import React, { useMemo } from 'react';
import AccountSelect, { findOption, groupAccountsByInstitution } from './accounts/AccountSelect.jsx';

/** Choices that are not an account. Kept out of the institution groups so they stay easy to reach. */
export const ACCOUNT_SELECT_NONE = '';
export const ACCOUNT_SELECT_SKIP = 'skip';
export const ACCOUNT_SELECT_CREATE = 'create';

/**
 * The import page's account picker: the budget's accounts, drawn by AccountLabel and grouped by
 * institution (see AccountSelect), behind a "Mapping" group of the choices that are not an account --
 * leave the label unmapped, skip it, or create an account for it.
 *
 * `accounts` is import/rules' available_accounts, which carry their latest balance and its date.
 */
export default function PlaidAccountSelect({ accounts, value, onChange, skipLabel, isDisabled, allowCreate }) {
	const { options, selected } = useMemo(
		() => buildOptions(accounts, value, skipLabel, allowCreate),
		[accounts, value, skipLabel, allowCreate]
	);

	return (
		<AccountSelect
			className='plaid_account_select'
			groups={options}
			value={selected}
			onChange={(option) => onChange(option ? option.value : ACCOUNT_SELECT_NONE)}
			isDisabled={isDisabled}
			placeholder='Not mapped'
			wrapValue
		/>
	);
}

function buildOptions(accounts, value, skipLabel, allowCreate) {
	const mapping = [
		{ value: ACCOUNT_SELECT_NONE, label: 'Not mapped' },
		{ value: ACCOUNT_SELECT_SKIP, label: skipLabel || "Don't import this account" },
	];
	/**
	 * Offered alongside the accounts rather than only when nothing matches: a file can name an account
	 * that looks like one already here and is not, and the person reading the row is the one who knows.
	 */
	if(allowCreate) {
		mapping.push({ value: ACCOUNT_SELECT_CREATE, label: 'Create a new account…' });
	}

	const options = [
		{ label: 'Mapping', badge: null, options: mapping },
		...groupAccountsByInstitution(accounts),
	];
	return { options, selected: findOption(options, value) };
}
